<template>
    <div class="max-w-3xl mx-auto p-4">
        <h1 class="text-2xl font-semibold text-theme-base mb-2">Tags</h1>
        <p class="text-sm text-theme-muted mb-6">
            Administra el catálogo de etiquetas de tu negocio. Estas etiquetas
            quedan disponibles para marcar productos y para configurar filtros
            de búsqueda.
        </p>

        <div class="flex flex-col gap-6">
            <div class="bg-theme-card rounded-xl p-6 flex flex-col gap-4">
                <section>
                    <h2 class="text-lg font-medium text-theme-base mb-2">
                        Agregar tag nuevo
                    </h2>
                    <p class="text-sm text-theme-muted mb-4">
                        El valor se sugiere automáticamente a partir del nombre,
                        pero puedes editarlo antes de agregarlo.
                    </p>
                    <div class="flex items-end gap-3">
                        <custom-input
                            :value="newTag.name"
                            label="Nombre"
                            class="w-full"
                            autocomplete="off"
                            @input="onNewTagNameInput"
                            @enter="addTag"
                        />
                        <custom-input
                            :value="newTag.value"
                            label="Valor (slug)"
                            class="w-full"
                            autocomplete="off"
                            @input="onNewTagValueInput"
                            @enter="addTag"
                        />
                        <app-button
                            border
                            class="shrink-0"
                            :disabled="!canAddNewTag"
                            @click="addTag"
                        >
                            Agregar
                        </app-button>
                    </div>
                </section>
            </div>

            <div class="bg-theme-card rounded-xl p-6 flex flex-col gap-4">
                <section>
                    <h2 class="text-lg font-medium text-theme-base mb-2">
                        Tags existentes
                    </h2>
                    <feature-manage
                        v-model="form.tags"
                        enabled
                        name-label="Nombre"
                        value-label="Valor (slug)"
                        items-empty-message="Todavía no tienes tags configurados"
                    />
                </section>
            </div>

            <div
                v-if="duplicateValues.length"
                class="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-theme-hover border border-theme-border text-theme-danger text-sm font-medium"
            >
                <span>⚠️</span>
                <span>
                    Hay valores repetidos:
                    <strong>{{ duplicateValues.join(", ") }}</strong>
                    . Cada tag debe tener un valor único.
                </span>
            </div>

            <div class="rounded-xl p-6 flex justify-center">
                <app-button
                    size="large"
                    :loading="isLoading"
                    :disabled="!!duplicateValues.length"
                    @click="save"
                >
                    Actualizar
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { OperationsModes } from "@/common/fields/operations-modes";
import { slugify } from "@/common/lib/transformers";

import AppButton from "@/components/utils/AppButton.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import FeatureManage from "@/components/utils/FeatureManage.vue";

export default {
    name: "SettingsTagsPage",
    components: { AppButton, CustomInput, FeatureManage },
    layout: "app",
    data: () => ({
        isLoading: false,
        form: {
            tags: []
        },
        newTag: {
            name: "",
            value: ""
        },
        newTagValueTouched: false
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        isMultiTenant() {
            return this.$operationMode === OperationsModes.multiTenant;
        },
        canAddNewTag() {
            return !!(this.newTag.name.trim() && this.newTag.value.trim());
        },
        duplicateValues() {
            const counts = {};
            this.form.tags.forEach((tag) => {
                counts[tag.value] = (counts[tag.value] || 0) + 1;
            });
            return Object.keys(counts).filter((value) => counts[value] > 1);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (
            !(await verifyPagePermission(
                "tenant:GetTagsSettings",
                "/app/settings"
            ))
        )
            return;
        if (this.isMultiTenant) {
            await this.$swal.fire({
                background: this.backgroundColor,
                type: "info",
                title: "No disponible",
                text: "El catálogo de tags no aplica en modo multi-tienda.",
                timer: 5000
            });
            this.$router.push("/app/settings");
            return;
        }
        await this.loadSettings();
    },
    methods: {
        ...mapActions("tenants", [
            "getTenantTagsSettings",
            "updateTenantTagsSettings"
        ]),
        async loadSettings() {
            this.$loading.show();
            try {
                const data = await this.getTenantTagsSettings();
                this.form.tags = Array.isArray(data.tags) ? [...data.tags] : [];
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        onNewTagNameInput(value) {
            this.newTag.name = value;
            if (!this.newTagValueTouched) {
                this.newTag.value = slugify(value);
            }
        },
        onNewTagValueInput(value) {
            this.newTag.value = value;
            this.newTagValueTouched = true;
        },
        addTag() {
            if (!this.canAddNewTag) return;
            this.form.tags = [
                ...this.form.tags,
                {
                    name: this.newTag.name.trim(),
                    value: this.newTag.value.trim()
                }
            ];
            this.newTag = { name: "", value: "" };
            this.newTagValueTouched = false;
        },
        async save() {
            if (this.duplicateValues.length) return;
            this.isLoading = true;
            try {
                await this.updateTenantTagsSettings({ tags: this.form.tags });
                this.$toast.success({
                    title: "Configuración actualizada",
                    text: "El catálogo de tags ha sido guardado."
                });
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        }
    }
};
</script>
